import debounce from 'lodash/debounce';
import { useImperativeHandle, useMemo, useState } from 'react';

import { countryRequiresZipCode } from '@/constants/countries';
import { Countries } from '@/utils/common';

import type { CreateOrderFormType } from '../create-order-schema';
import type { AddressDetails } from '@/components/address-autocomplete';
import type { RefObject } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

export type { PlaceType } from '@/components/address-autocomplete';

export interface AddressInputRef {
  update: (value: string) => void;
  setText: (value: string) => void;
  selectSuggest: (value: unknown) => void;
}

interface UseAddressInputProps {
  name: 'origin' | 'destination';
  setValue: UseFormSetValue<CreateOrderFormType>;
  country?: Countries;
  onZipCodeNotFound?: (notFound: boolean) => void;
  findAddressComponent: (
    components: google.maps.GeocoderAddressComponent[],
    type: string,
  ) => string;
  ref?: RefObject<AddressInputRef | null>;
}

const getGeocoder = (): google.maps.Geocoder | null => {
  if (typeof window !== 'undefined' && window.google?.maps) {
    return new window.google.maps.Geocoder();
  }
  return null;
};

export const useAddressInput = (props: UseAddressInputProps) => {
  const { name, setValue, country, onZipCodeNotFound, findAddressComponent, ref } = props;

  const [inputValue, setInputValue] = useState('');

  const countryCode = useMemo(() => {
    const countryMap: Record<Countries, string> = {
      MEX: 'mx',
      COL: 'co',
      CHL: 'cl',
      PER: 'pe',
    };
    return country ? countryMap[country] : 'mx';
  }, [country]);

  const populateFromComponents = (
    components: google.maps.GeocoderAddressComponent[],
    geometry: google.maps.GeocoderGeometry,
  ) => {
    const zipcode = findAddressComponent(components, 'postal_code');
    const state = findAddressComponent(components, 'administrative_area_level_1');
    const city =
      findAddressComponent(components, 'locality') ||
      findAddressComponent(components, 'administrative_area_level_2') ||
      findAddressComponent(components, 'administrative_area_level_3') ||
      state ||
      '';
    const cologne =
      findAddressComponent(components, 'sublocality_level_1') ||
      findAddressComponent(components, 'neighborhood') ||
      findAddressComponent(components, 'sublocality') ||
      '';
    const streetNumber = findAddressComponent(components, 'street_number');

    if (zipcode) setValue(`${name}.zipCode` as const, zipcode, { shouldValidate: true });
    if (city) setValue(`${name}.city` as const, city, { shouldValidate: true });
    if (state) setValue(`${name}.state` as const, state, { shouldValidate: true });
    if (cologne) setValue(`${name}.cologne` as const, cologne, { shouldValidate: true });
    if (streetNumber) {
      setValue(`${name}.externalNumber` as const, streetNumber, { shouldValidate: true });
    }
    setValue(`${name}.country` as const, country || 'MEX', { shouldValidate: true });
    setValue(`${name}.lat` as const, String(geometry.location.lat()), { shouldValidate: true });
    setValue(`${name}.lng` as const, String(geometry.location.lng()), { shouldValidate: true });

    if (onZipCodeNotFound && countryRequiresZipCode(country)) {
      const zipcodeNotFound = !zipcode || zipcode === '';
      onZipCodeNotFound(zipcodeNotFound);
    }
  };

  const onAddressSelect = (details: AddressDetails) => {
    setInputValue(details.address);
    setValue(`${name}.address` as const, details.address, { shouldValidate: true });
    setValue(`${name}.zipCode` as const, details.zipCode, { shouldValidate: true });
    setValue(`${name}.city` as const, details.city, { shouldValidate: true });
    setValue(`${name}.state` as const, details.state, { shouldValidate: true });
    setValue(`${name}.cologne` as const, details.neighborhood, { shouldValidate: true });
    if (details.streetNumber) {
      setValue(`${name}.externalNumber` as const, details.streetNumber, { shouldValidate: true });
    }
    setValue(`${name}.country` as const, country || 'MEX', { shouldValidate: true });
    setValue(`${name}.lat` as const, String(details.latitude), { shouldValidate: true });
    setValue(`${name}.lng` as const, String(details.longitude), { shouldValidate: true });

    if (onZipCodeNotFound && countryRequiresZipCode(country)) {
      const zipcodeNotFound = !details.zipCode || details.zipCode === '';
      onZipCodeNotFound(zipcodeNotFound);
    }
  };

  const debouncedGeocode = useMemo(
    () =>
      debounce((addressText: string) => {
        if (!addressText || addressText.trim().length < 4) return;
        const geocoder = getGeocoder();
        if (!geocoder) return;

        geocoder.geocode(
          {
            address: addressText,
            componentRestrictions: { country: countryCode },
          },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              populateFromComponents(results[0].address_components, results[0].geometry);
            }
          },
        );
      }, 600),
    [countryCode],
  );

  const onChangeInputValue = (value: string) => {
    setInputValue(value);
    if (!value || value.trim() === '') {
      setValue(`${name}.lat` as const, '');
      setValue(`${name}.lng` as const, '');
      setValue(`${name}.zipCode` as const, '', { shouldValidate: true });
      setValue(`${name}.city` as const, '', { shouldValidate: true });
      setValue(`${name}.cologne` as const, '', { shouldValidate: true });
      setValue(`${name}.state` as const, '', { shouldValidate: true });
      setValue(`${name}.externalNumber` as const, '', { shouldValidate: true });
      if (onZipCodeNotFound) {
        onZipCodeNotFound(false);
      }
      return;
    }

    debouncedGeocode(value);
  };

  useImperativeHandle(ref, () => ({
    setText: (value: string) => {
      setInputValue(value);
    },
    update: (value: string) => {
      setInputValue(value);
      setValue(`${name}.address` as const, value, { shouldValidate: true });

      if (value) {
        const geocoder = getGeocoder();
        if (!geocoder) return;

        geocoder.geocode(
          {
            address: value,
            componentRestrictions: { country: countryCode },
          },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              populateFromComponents(results[0].address_components, results[0].geometry);
            }
          },
        );
      }
    },
    selectSuggest: (_value: unknown) => {},
  }));

  return {
    inputValue,
    onAddressSelect,
    onChangeInputValue,
  };
};
